import { Maximize2, Minus, Plus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const ZOOM_FACTOR = 1.12;

interface WorkspaceCanvasProps {
  zoom: number;
  panX: number;
  panY: number;
  locked?: boolean;
  onZoomAt: (factor: number, mx: number, my: number) => void;
  onSetZoom: (zoom: number, mx: number, my: number) => void;
  onSetPan: (panX: number, panY: number) => void;
  onFitContent: (containerW: number, containerH: number, contentW: number, contentH: number) => void;
  onSizeChange?: (containerW: number, containerH: number, contentW: number, contentH: number) => void;
  children: ReactNode;
}

export default function WorkspaceCanvas({
  zoom,
  panX,
  panY,
  locked = false,
  onZoomAt,
  onSetZoom,
  onSetPan,
  onFitContent,
  onSizeChange,
  children,
}: WorkspaceCanvasProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ zoom, panX, panY });
  stateRef.current = { zoom, panX, panY };

  const dragRef = useRef({ active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1, midX: 0, midY: 0 });

  const [spaceDown, setSpaceDown] = useState(false);

  // Measure container and content sizes
  useEffect(() => {
    const c = containerRef.current;
    const t = contentRef.current;
    if (!c || !t || !onSizeChange) return;

    const obs = new ResizeObserver(() => {
      onSizeChange(c.offsetWidth, c.offsetHeight, t.offsetWidth, t.offsetHeight);
    });
    obs.observe(c);
    obs.observe(t);
    onSizeChange(c.offsetWidth, c.offsetHeight, t.offsetWidth, t.offsetHeight);
    return () => obs.disconnect();
  }, [onSizeChange]);

  // Wheel: ctrl/meta = zoom, plain = pan
  useEffect(() => {
    if (locked) return;
    const el = containerRef.current;
    if (!el) return;

    function handle(e: WheelEvent) {
      e.preventDefault();
      const rect = el!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      if (e.ctrlKey || e.metaKey) {
        onZoomAt(e.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR, mx, my);
      } else {
        const { panX: px, panY: py } = stateRef.current;
        onSetPan(px - e.deltaX, py - e.deltaY);
      }
    }

    el.addEventListener('wheel', handle, { passive: false });
    return () => el.removeEventListener('wheel', handle);
  }, [locked, onZoomAt, onSetPan]);

  // Mouse drag (window-level to handle cursor moving outside)
  useEffect(() => {
    if (locked) return;
    function onMove(e: MouseEvent) {
      if (!dragRef.current.active) return;
      onSetPan(
        dragRef.current.startPanX + e.clientX - dragRef.current.startX,
        dragRef.current.startPanY + e.clientY - dragRef.current.startY,
      );
    }
    function onUp() {
      if (dragRef.current.active) {
        dragRef.current.active = false;
        if (containerRef.current) containerRef.current.style.cursor = '';
      }
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [locked, onSetPan]);

  // Space bar: toggle pan mode cursor
  useEffect(() => {
    if (locked) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.code === 'Space' && !e.repeat) {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
        e.preventDefault();
        setSpaceDown(true);
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code === 'Space') setSpaceDown(false);
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [locked]);

  // Touch: single finger pan, two fingers pinch
  useEffect(() => {
    if (locked) return;
    const el = containerRef.current;
    if (!el) return;

    function onTouchStart(e: TouchEvent) {
      if (e.touches.length === 2) {
        e.preventDefault();
        const t0 = e.touches.item(0)!;
        const t1 = e.touches.item(1)!;
        const rect = el!.getBoundingClientRect();
        pinchRef.current = {
          active: true,
          startDist: Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY),
          startZoom: stateRef.current.zoom,
          midX: (t0.clientX + t1.clientX) / 2 - rect.left,
          midY: (t0.clientY + t1.clientY) / 2 - rect.top,
        };
        dragRef.current.active = false;
      } else if (e.touches.length === 1) {
        const t0 = e.touches.item(0)!;
        dragRef.current = {
          active: true,
          startX: t0.clientX,
          startY: t0.clientY,
          startPanX: stateRef.current.panX,
          startPanY: stateRef.current.panY,
        };
      }
    }

    function onTouchMove(e: TouchEvent) {
      e.preventDefault();
      if (e.touches.length === 2 && pinchRef.current.active) {
        const t0 = e.touches.item(0)!;
        const t1 = e.touches.item(1)!;
        const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
        onSetZoom(
          pinchRef.current.startZoom * (dist / pinchRef.current.startDist),
          pinchRef.current.midX,
          pinchRef.current.midY,
        );
      } else if (e.touches.length === 1 && dragRef.current.active) {
        const t0 = e.touches.item(0)!;
        onSetPan(
          dragRef.current.startPanX + t0.clientX - dragRef.current.startX,
          dragRef.current.startPanY + t0.clientY - dragRef.current.startY,
        );
      }
    }

    function onTouchEnd() {
      pinchRef.current.active = false;
      dragRef.current.active = false;
    }

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [locked, onSetZoom, onSetPan]);

  function onMouseDown(e: React.MouseEvent) {
    if (locked || e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (!spaceDown && target.closest('button, input, textarea, a, [role="button"], [draggable]')) return;
    e.preventDefault();
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startPanX: panX,
      startPanY: panY,
    };
  }

  const cursor = locked ? 'default' : spaceDown ? 'grab' : 'default';

  return (
    <div
      ref={containerRef}
      className='relative h-full w-full overflow-hidden select-none'
      style={{ cursor }}
      onMouseDown={onMouseDown}
    >
      <div
        ref={contentRef}
        className={locked ? 'h-full' : undefined}
        style={
          locked
            ? undefined
            : {
                transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
                transformOrigin: '0 0',
                willChange: 'transform',
              }
        }
      >
        {children}
      </div>

      {/* Zoom controls — hidden when canvas is locked */}
      {!locked && (
        <div className='pointer-events-auto absolute bottom-4 left-4 flex items-center gap-1 rounded-lg border border-ws-border bg-ws-surface px-2 py-1.5 shadow-md'>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (!containerRef.current) return;
              const { offsetWidth: w, offsetHeight: h } = containerRef.current;
              onZoomAt(1 / ZOOM_FACTOR, w / 2, h / 2);
            }}
            className='flex h-7 w-7 items-center justify-center rounded hover:bg-ws-hover'
            aria-label={t('experience.tools.zoomOut')}
          >
            <Minus className='h-3.5 w-3.5 text-ws-text' />
          </button>
          <span className='min-w-[38px] text-center text-xs font-medium text-ws-text'>
            {Math.round(zoom * 100)}%
          </span>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (!containerRef.current) return;
              const { offsetWidth: w, offsetHeight: h } = containerRef.current;
              onZoomAt(ZOOM_FACTOR, w / 2, h / 2);
            }}
            className='flex h-7 w-7 items-center justify-center rounded hover:bg-ws-hover'
            aria-label={t('experience.tools.zoomIn')}
          >
            <Plus className='h-3.5 w-3.5 text-ws-text' />
          </button>
          <div className='mx-0.5 h-4 w-px bg-ws-border' />
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              const c = containerRef.current;
              const t = contentRef.current;
              if (!c || !t) return;
              onFitContent(c.offsetWidth, c.offsetHeight, t.offsetWidth, t.offsetHeight);
            }}
            className='flex h-7 w-7 items-center justify-center rounded hover:bg-ws-hover'
            aria-label={t('experience.tools.fitToScreen')}
          >
            <Maximize2 className='h-3.5 w-3.5 text-ws-text' />
          </button>
        </div>
      )}
    </div>
  );
}
